import React from "react";
import {
  Box,
  Chip,
  Select,
  MenuItem,
  Pagination,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@mui/material";
import {
  makeStyles
} from "@mui/styles";
import {
  TableCeilSort,
  VisibleContent,
  SkeletonLoadingTable
} from "../../../../components";
import { LocationGoLink, IconGoLink } from "../../../../components/SectionGoButtons";
import moment from "moment";
import {
  canNavigateToAddresses,
  canNavigateToComplex,
  getMaterialsToAddressesUrl,
  getMaterialsToComplexUrl,
} from "../../../../utils/sectionNavigation";

const TableCustom = ({ items, filter, totalPages, totalCount, isLoading, onSearch, onChangeFilter }) => {
  const classes = useStyles();

  const handleChangePage = (e, p) => {
    onChangeFilter({
      ...filter,
      page: p
    }, true)
  }
  const handleChangeSort = (values) => {
    onChangeFilter({
      ...filter,
      ...values
    }, true)
  }
  const handleChangeLimit = (e) => {
    onChangeFilter({
      ...filter,
      page: 1,
      limit: e.target.value,
    }, true)
  }

  return (
    <Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCeilSort
              name="datetime"
              sort_field={filter.sort_field}
              sort_direction={filter.sort_direction}
              onChange={handleChangeSort}
            >Дата и время</TableCeilSort>
            <TableCell>Госномер</TableCell>
            <TableCell>Событие</TableCell>
            <TableCell>Местоположение</TableCell>
            <TableCell>Направление</TableCell>
            <TableCell>Номер устройства</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <VisibleContent visible={!isLoading}>
            {items.map((item, idx) => (
              <RowItem
                key={`RowItem-${item.id || idx}`}
                item={item}
              />
            ))}
          </VisibleContent>
          <VisibleContent visible={isLoading}>
            <SkeletonLoadingTable countRows={10} countColumns={6}/>
          </VisibleContent>
        </TableBody>
      </Table>

      <Box className={classes.paginationBar}>
        <span className={classes.paginationInfo}>
          Найдено {totalCount.toLocaleString('ru-RU')} записей
        </span>
        <Box className={classes.paginationRight}>
          <Box className={classes.paginationLimit}>
            <span>Выводить по</span>
            <Select
              value={filter.limit}
              onChange={handleChangeLimit}
              size="small"
              sx={{ minWidth: 70, height: 32, fontSize: 14 }}
            >
              <MenuItem value={15}>15</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Box>
          {totalPages > 1 && (
            <Box className={classes.paginationPages}>
              <span>Страница {filter.page} из {totalPages}</span>
              <Pagination
                count={totalPages}
                page={filter.page}
                onChange={handleChangePage}
                size="small"
                hidePrevButton={false}
                hideNextButton={false}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
const RowItem = ({ item }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [direction] = React.useState(() => {
    if (item?.direction === '0') return "Попутное";
    if (item?.direction === '1') return "Встречное";
    return ''
  });

  const locationHref = getMaterialsToAddressesUrl(item);
  const deviceHref = getMaterialsToComplexUrl(item);
  const showLocationLink = canNavigateToAddresses(item);
  const showDeviceLink = canNavigateToComplex(item);

  const renderEvent = () => {
    if (item.event_type === 'violation') {
      return (<Chip label="Нарушение ПДД" size="small" sx={{ backgroundColor: '#FFEAE7', color: '#E85949', fontWeight: 600, fontSize: 12 }} />)
    }
    if (item.event_type === 'no_violation') {
      return (<Chip label="Без нарушений" size="small" sx={{ backgroundColor: '#E4FBF1', color: '#1CA673', fontWeight: 600, fontSize: 12 }} />)
    }
    return (<Chip label="Для распознавания ГРЗ" size="small" sx={{ backgroundColor: '#FFF4E6', color: '#F59E0B', fontWeight: 600, fontSize: 12 }} />)
  }

  return (
    <TableRow
      hover
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableCell sx={{ whiteSpace: "nowrap" }}>{moment(item.datetime).format('DD.MM.YYYY  HH:mm')}</TableCell>
      <TableCell>{item?.license_plate || 'Н/Д'}</TableCell>
      <TableCell>{renderEvent()}</TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box component="span" sx={{ minWidth: 0 }}>{item?.sensor_location}</Box>
          {showLocationLink && locationHref && (
            <LocationGoLink href={locationHref} visible={isHovered} />
          )}
        </Box>
      </TableCell>
      <TableCell>{direction}</TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box component="span" sx={{ whiteSpace: "nowrap" }}>{item?.sensor_serial}</Box>
          {showDeviceLink && deviceHref && (
            <IconGoLink href={deviceHref} visible={isHovered} />
          )}
        </Box>
      </TableCell>
    </TableRow>
  )
}

const useStyles = makeStyles(() => ({
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 0",
  },
  paginationInfo: {
    fontSize: 14,
    color: "#71717A",
    fontWeight: 400,
  },
  paginationRight: {
    display: "flex",
    alignItems: "center",
    gap: 24,
  },
  paginationLimit: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#71717A",
  },
  paginationPages: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#71717A",
    "& .MuiPagination-root": {
      "& .MuiPaginationItem-root": {
        minWidth: 28,
        height: 28,
      },
    },
  },
}));

export default TableCustom
